import { useBrand } from "../brand/BrandProvider";
import { normalizeBrandLogoUrl } from "../../api/brandApi";

type Props = {
  /** Show the full wordmark (symbol + text) instead of just the symbol. */
  wordmark?: boolean;
  className?: string;
  title?: string;
};

/**
 * Tenant brand mark used in the top bar and (optionally) the footer.
 *
 * When the resolved brand exposes a `logoUrl` the component renders an
 * <img> straight off /api/brand/logo so each manager can ship their own
 * artwork. Without an uploaded logo we fall back to the embedded SVG mark
 * and let it inherit the active brand colour through `currentColor`.
 *
 * Two variants:
 *   - symbol only  (default)         — for compact spots like the TopBar
 *   - full wordmark (`wordmark`)     — symbol + brand text (footer etc.)
 */
export function BrandLogo({
  wordmark = false,
  className,
  title,
}: Props) {
  const { brand } = useBrand();
  // For a11y we need something non-empty in aria-label / alt. portalName
  // is allowed to be blank (TopBar then renders only the logo), so fall
  // through to documentTitle (always populated) before the hardcoded
  // "Brand" sentinel.
  const label = title || brand.portalName || brand.documentTitle || "Brand";

  const logoSrc = normalizeBrandLogoUrl(brand.logoUrl);

  if (logoSrc) {
    return (
      <img
        src={logoSrc}
        alt={label}
        className={className}
        // Width/height come from the parent container (e.g. .brand-mark
        // in the TopBar) so the image follows the host layout's
        // intrinsic aspect ratio instead of being squashed into a square.
        style={{ display: "block", width: "auto", height: "100%" }}
      />
    );
  }

  if (wordmark) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 110 42"
        className={className}
        role="img"
        aria-label={label}
        style={{ color: "var(--portal-primary)" }}
      >
        <title>{label}</title>
        <g fill="currentColor">
          <path d="M100.7,13.3c-3.4,0-6.2,0.6-8.8,1.6V32h5.7V18.3c0.8-0.4,1.9-0.6,2.9-0.6c3.1,0,3.7,1.9,3.7,5.4V32h5.7v-9.7 C110,16.8,108.2,13.3,100.7,13.3z" />
          <rect x="46" y="6.9" width="5.7" height="25.1" />
          <polygon points="64.5,13.9 58,13.9 51.7,22.1 58.3,31.9 65.1,31.9 58,21.9" />
          <path d="M77.1,13.2c-3,0-6.2,0.7-8.6,1.6V39h5.7v-6.9c7.2,1.3,13-1.7,12.9-9.4C87.1,16.6,83.9,13.2,77.1,13.2z M76.8,28.1c-0.9,0-2-0.2-2.6-0.4v-9.6c3.4-1.4,7-0.4,7,4.6C81.2,26.2,79.9,28.1,76.8,28.1z" />
          <path d="M14.5,30.9c3.7-1,7.7-1,11.5,0.1l0.9-2.6c-4.4-1.3-9.2-1.3-13.6,0l0.9,2.6L14.5,30.9z" />
          <polygon points="17.9,14.1 20,16.3 22.1,14.1 20,11.9" />
          <path d="M14.4,20.2c-0.2,1.4,0.6,3.5,2.1,5.9c1-0.2,1.9-0.2,2.9-0.2l-0.3-0.5c-1.6-3.1-1.9-4-1.9-4.7 c0-1,0.8-1.1,1.9-1v-2.4C16.6,16.5,14.6,17.9,14.4,20.2z" />
          <path d="M12.7,20.1c-1.7-0.1-3,1-3,2.5c-0.1,1.1,0.6,2.4,2.7,4.2c0.9-0.3,1.8-0.5,2.7-0.7c-1.6-1.6-2.9-2.9-2.7-3.4 c0-0.1,0-0.4,0.5-0.5l0.4-0.1c-0.2-0.7-0.3-1.4-0.2-2.1H12.7z" />
          <path d="M20.6,25.9c0.9,0,1.9,0.1,2.9,0.2c1.5-2.4,2.3-4.5,2.1-5.9c-0.3-2.3-2.3-3.7-4.7-2.8v2.4 c1.1-0.1,1.9,0,1.9,1c0,0.7-0.3,1.6-1.9,4.7L20.6,25.9z" />
          <path d="M25.3,25.7l-0.5,0.5c0.9,0.2,1.9,0.4,2.7,0.7c2.1-1.8,2.8-3.1,2.7-4.2c0-1.7-1.5-2.7-3.3-2.5 c0,0.7,0,1.4-0.2,2.1l0.4,0.1c0.5,0.1,0.5,0.4,0.5,0.5C27.6,23.3,27.3,23.8,25.3,25.7z" />
          <path d="M6.4,20.6c0.5-0.3,1.5-1,2.7-1.6c-1-2.4-1.6-4.6-1.6-6.3c0-3.6,0.9-6.6,2.4-8.1c4-4,11.8,1.1,15.4,6.6 c1.1-0.2,2.4-0.4,3.5-0.4C25.4,4.4,18.1,0,13.5,0C7.3,0,4.5,6.4,4.5,12.7C4.5,15.8,5.5,18.2,6.4,20.6z" />
          <path d="M29.3,12.7c-1.8,0-2.9,0.3-2.9,0.3c0.4,0.9,0.7,2,0.8,3.1c0.7-0.1,1.4-0.2,2.1-0.2c4,0,6.4,2.3,6.5,5.1 c0,1.5-0.8,3.5-1.6,5.4c-0.4,1-1.1,2.9-1.5,4.5c-0.7,2.7-2.6,10.2-6.9,7.3c-1,0.4-2.3,1.1-3.4,1.3c2,1.8,3.4,2.4,5.4,2.4 c5.3,0,7.2-7.5,7.9-10.3c0.7-3.1,3.3-7.4,3.2-10.7C38.9,16,34.4,12.5,29.3,12.7z" />
          <path d="M24.3,36.9c-0.8-0.8-1.6-1.6-2.3-2.4c-5.5,2.1-12.5-0.4-16.5-4.3c-1.4-1.4-2.3-3.1-2.3-4.3 c0-1.8,0.6-3,1.8-4c-0.4-1-0.8-2-1.2-3c-2.5,1.6-3.8,4-3.8,7c0,2.1,1.1,4.5,3.2,6.6C7.4,36.7,17.1,40.6,24.3,36.9z" />
        </g>
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 42"
      className={className}
      role="img"
      aria-label={label}
      style={{ color: "var(--portal-primary)" }}
    >
      <title>{label}</title>
      <g fill="currentColor">
        <path d="M14.5,30.9c3.7-1,7.7-1,11.5,0.1l0.9-2.6c-4.4-1.3-9.2-1.3-13.6,0l0.9,2.6L14.5,30.9z" />
        <polygon points="17.9,14.1 20,16.3 22.1,14.1 20,11.9" />
        <path d="M14.4,20.2c-0.2,1.4,0.6,3.5,2.1,5.9c1-0.2,1.9-0.2,2.9-0.2l-0.3-0.5c-1.6-3.1-1.9-4-1.9-4.7 c0-1,0.8-1.1,1.9-1v-2.4C16.6,16.5,14.6,17.9,14.4,20.2z" />
        <path d="M12.7,20.1c-1.7-0.1-3,1-3,2.5c-0.1,1.1,0.6,2.4,2.7,4.2c0.9-0.3,1.8-0.5,2.7-0.7c-1.6-1.6-2.9-2.9-2.7-3.4 c0-0.1,0-0.4,0.5-0.5l0.4-0.1c-0.2-0.7-0.3-1.4-0.2-2.1H12.7z" />
        <path d="M20.6,25.9c0.9,0,1.9,0.1,2.9,0.2c1.5-2.4,2.3-4.5,2.1-5.9c-0.3-2.3-2.3-3.7-4.7-2.8v2.4 c1.1-0.1,1.9,0,1.9,1c0,0.7-0.3,1.6-1.9,4.7L20.6,25.9z" />
        <path d="M25.3,25.7l-0.5,0.5c0.9,0.2,1.9,0.4,2.7,0.7c2.1-1.8,2.8-3.1,2.7-4.2c0-1.7-1.5-2.7-3.3-2.5 c0,0.7,0,1.4-0.2,2.1l0.4,0.1c0.5,0.1,0.5,0.4,0.5,0.5C27.6,23.3,27.3,23.8,25.3,25.7z" />
        <path d="M6.4,20.6c0.5-0.3,1.5-1,2.7-1.6c-1-2.4-1.6-4.6-1.6-6.3c0-3.6,0.9-6.6,2.4-8.1c4-4,11.8,1.1,15.4,6.6 c1.1-0.2,2.4-0.4,3.5-0.4C25.4,4.4,18.1,0,13.5,0C7.3,0,4.5,6.4,4.5,12.7C4.5,15.8,5.5,18.2,6.4,20.6z" />
        <path d="M29.3,12.7c-1.8,0-2.9,0.3-2.9,0.3c0.4,0.9,0.7,2,0.8,3.1c0.7-0.1,1.4-0.2,2.1-0.2c4,0,6.4,2.3,6.5,5.1 c0,1.5-0.8,3.5-1.6,5.4c-0.4,1-1.1,2.9-1.5,4.5c-0.7,2.7-2.6,10.2-6.9,7.3c-1,0.4-2.3,1.1-3.4,1.3c2,1.8,3.4,2.4,5.4,2.4 c5.3,0,7.2-7.5,7.9-10.3c0.7-3.1,3.3-7.4,3.2-10.7C38.9,16,34.4,12.5,29.3,12.7z" />
        <path d="M24.3,36.9c-0.8-0.8-1.6-1.6-2.3-2.4c-5.5,2.1-12.5-0.4-16.5-4.3c-1.4-1.4-2.3-3.1-2.3-4.3 c0-1.8,0.6-3,1.8-4c-0.4-1-0.8-2-1.2-3c-2.5,1.6-3.8,4-3.8,7c0,2.1,1.1,4.5,3.2,6.6C7.4,36.7,17.1,40.6,24.3,36.9z" />
      </g>
    </svg>
  );
}
